import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function ApartmentSearchResults({ results, isLoading, filters }) {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // 페이지네이션 계산
  const totalPages = Math.ceil(results.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentResults = results.slice(startIndex, endIndex);
  
  // 금액 포맷팅
  const formatPrice = (price) => {
    if (!price) return '-';
    const numPrice = parseInt(price);
    if (numPrice >= 10000) {
      const uk = Math.floor(numPrice / 10000);
      const chun = numPrice % 10000;
      return chun > 0 ? `${uk}억 ${chun.toLocaleString()}만` : `${uk}억`;
    }
    return `${numPrice.toLocaleString()}만`;
  };
  
  // 평형 계산 (1평 = 3.3058㎡)
  const calculatePyeong = (area) => {
    if (!area) return '-';
    const pyeong = parseFloat(area) / 3.3058;
    // 반올림하여 정수로 표시
    return Math.round(pyeong);
  };
  
  // 하락률 포맷팅
  const formatPercentage = (value) => {
    if (!value) return '-';
    const num = parseFloat(value);
    return num > 0 ? `+${num.toFixed(1)}%` : `${num.toFixed(1)}%`;
  };
  
  // 아파트 상세 페이지로 이동
  const handleRowClick = (apartmentNumber) => {
    navigate(`/apartment/${apartmentNumber}`);
  };
  
  if (isLoading) {
    return (
      <div className="bg-[#FFFEF6] border border-gray-200 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
          <p className="mt-2">검색 중...</p>
        </div>
      </div>
    );
  }
  
  if (results.length === 0) {
    return (
      <div className="bg-[#FFFEF6] border border-gray-200 rounded-lg shadow-md p-6">
        <p className="text-center text-gray-500 py-8">
          검색 결과가 없습니다. 필터를 조정해보세요.
        </p>
      </div>
    );
  }
  
  return (
    <div className="bg-[#FFFEF6] border border-gray-200 rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
      <div className="mb-4">
        <h2 className="text-xl font-semibold">
          검색 결과 ({results.length}개)
        </h2>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full table-auto">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                순위
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                아파트명
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                면적
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                매매가
              </th>
              {filters.priceChange === 'jeonse_ratio' && (
                <>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    전세가
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    전세가율
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    갭
                  </th>
                </>
              )}
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                최고가
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                변동률
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                세대수
              </th>
              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                매물수
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentResults.map((apt, index) => (
              <tr
                key={`${apt.apartment_number}-${apt.size_no}`}
                className="hover:bg-[#fffdec] cursor-pointer transition-colors duration-150"
                onClick={() => handleRowClick(apt.apartment_number)}
              >
                <td className="px-4 py-2 whitespace-nowrap text-sm">
                  {startIndex + index + 1}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                  {apt.apartment_name}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm">
                  {apt.exclusive_area}㎡
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-blue-600">
                  {formatPrice(apt.sale_price)}
                </td>
                {filters.priceChange === 'jeonse_ratio' && (
                  <>
                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-purple-600">
                      {formatPrice(apt.jeonse_price)}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm">
                      <span className="font-medium">
                        {apt.jeonse_ratio ? `${parseFloat(apt.jeonse_ratio).toFixed(1)}%` : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap text-sm">
                      <span className={apt.trading_gap && parseFloat(apt.trading_gap) > 0 ? 'text-green-600 font-medium' : 'text-gray-600'}>
                        {formatPrice(apt.trading_gap)}
                      </span>
                    </td>
                  </>
                )}
                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-600">
                  <div>
                    {formatPrice(apt.previous_peak_price_price)}
                    {apt.previous_peak_price_date && (
                      <div className="text-xs text-gray-400">
                        {apt.previous_peak_price_date}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm">
                  <div>
                    <span className={apt.peak_comparison && parseFloat(apt.peak_comparison) < 0 ? 'text-red-600' : 'text-green-600'}>
                      {formatPercentage(apt.peak_comparison)}
                    </span>
                    {apt.current_vs_peak_price && (
                      <div className="text-xs text-gray-500">
                        {formatPrice(apt.current_vs_peak_price)}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm">
                  {apt.total_households ? `${apt.total_households}세대` : '-'}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-sm">
                  {apt.deal_count || '0'}건
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center mt-6 space-x-2">
          <button
            className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-100 transition-colors"
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            이전
          </button>
          
          <div className="flex space-x-1">
            {Array.from({ length: Math.min(10, totalPages) }, (_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  className={`px-3 py-1 rounded transition-colors ${
                    currentPage === pageNum
                      ? 'bg-green-600 text-white'
                      : 'border border-gray-300 hover:bg-gray-100'
                  }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            {totalPages > 10 && (
              <span className="px-2 py-1">... {totalPages}</span>
            )}
          </div>
          
          <button
            className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50 hover:bg-gray-100 transition-colors"
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            다음
          </button>
        </div>
      )}
      
      {/* 결과 요약 */}
      <div className="mt-6 p-4 bg-[#FFFEF6] border border-gray-200 rounded">
        <h3 className="font-semibold mb-2">검색 조건 요약</h3>
        <div className="text-sm text-gray-600">
          {filters.districts.length > 0 && <p>• 선택된 구: {filters.districts.length}개</p>}
          {filters.households.min && <p>• 세대수: {filters.households.min}세대 이상</p>}
          {filters.area.min && <p>• 면적: {filters.area.min}㎡ ~ {filters.area.max || ''}㎡</p>}
          {filters.price.min && <p>• 가격: {formatPrice(filters.price.min)} ~ {filters.price.max ? formatPrice(filters.price.max) : ''}</p>}
          {filters.subwayTime && <p>• 역세권: 도보 {filters.subwayTime}분 이내</p>}
        </div>
      </div>
    </div>
  );
}